import { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { editCheckIn } from '@/api/client';
import type { CheckInDto } from '@/api/types';

/**
 * ---- ⭐⭐ THE NUMBER THE DRIVER OWED, TYPED AT THE DESK (#73) ---------------
 *
 * > **Pete, 2026-09-23:** *"Allow 123 as an out for the scheduler so he/she can
 * > tell the driver to finish the checkin and bring back the number when
 * > collecting paperwork. This should be an alert on the driver card in our
 * > alert section and lets add a icon next to the driversid scan icon like 123
 * > where a screen pops in dockscheduler and the scheduler can enter either
 * > trailer, container or license plate for the driver"*
 *
 * ⭐ THE GATE ASKS AND THE DESK ANSWERS. A driver who does not have his number
 * types the bypass code at the kiosk; nothing is stored, a flag is set, and the
 * card carries an alert until somebody fills this in. That is the whole loop,
 * and this dialog is its second half.
 *
 * ---- ⛔ WHY THIS IS NOT JUST "EDIT THIS ARRIVAL…" --------------------------
 *
 * ⚠️ The edit popup has containerNumber and trailerNumber among a dozen other
 * fields — and it has NO PLATE AT ALL (its `before` map never listed one). So
 * the three fields that actually answer the question are not in one place
 * anywhere else, and two of them sit beside eight that are nothing to do with
 * this.
 *
 * ⭐ AND THE ALERT NAMES ITS OWN CURE. A scheduler reading *"owes a trailer or
 * container number"* clicks the sentence and lands on exactly those fields —
 * the same idiom as the licence warning, which opens the licence scan.
 *   ** AN ALERT THAT DOES NOT OPEN THE THING THAT CLEARS IT IS A NOTIFICATION,
 *      NOT A TASK. **
 *
 * ---- ⛔⛔ NOTHING HERE DECIDES WHAT IS VALID -------------------------------
 *
 * ⛔ THE BYPASS CODE IS NOT WRITTEN IN THIS FILE AND MUST NOT BE. `web/` is a
 * separate tsconfig project and cannot import `src/lib/vehicleNumber.ts`, so a
 * check here would have to RESTATE the code — a second definition on the one
 * screen whose job is to replace it.
 *
 * ⭐ THE ROUTE REFUSES IT (`badVehicleValue` in functions/driverCheckIn.ts) and
 * this dialog shows the sentence it sends back. One definition, and the refusal
 * reaches every caller rather than this one screen.
 *   ** A RULE THE CLIENT KEEPS IS NOT A RULE. IT IS A COURTESY. **
 *
 * ⚠️ SO THE SAVE BUTTON IS NOT GATED ON LENGTH EITHER. Greying it out would
 * need the minimum, which is the same borrowed fact; an enabled button and a
 * clear refusal is honest, and it is one round trip.
 *
 * ---- ⭐ AND THE FLAG IS CLEARED BY THE SERVER, NOT BY THIS SCREEN ----------
 *
 * `editCheckIn` derives VehicleNumberPending from what the row ENDS UP holding.
 * Filling any one of these in clears the alert; blanking the last one puts the
 * chase back on. This dialog posts fields and reloads — it never touches the
 * flag, and there is no second rule here to fall out of step with that one.
 */
export function VehicleNumberDialog({
    open, checkIn, getToken, onClose, onSaved,
}: {
    open: boolean;
    /** Null while shut. ⚠️ Read on every render — never copied into state. */
    checkIn: CheckInDto | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [trailerNumber, setTrailerNumber] = useState('');
    const [containerNumber, setContainerNumber] = useState('');
    const [trailerPlate, setTrailerPlate] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * ⛔ RE-SEEDED FROM THE ARRIVAL EVERY TIME IT OPENS, keyed on the check-in id.
     *
     * ⚠️ Without the id in the deps the second driver opened would inherit the
     * first one's boxes — and these three fields are exactly the ones where a
     * carried-over value looks like a real answer. The rail polls, so this must
     * not re-run on every refresh either, which is why it is the ID and not the
     * object.
     */
    useEffect(() => {
        if (!open || !checkIn) return;
        setTrailerNumber(checkIn.trailerNumber ?? '');
        setContainerNumber(checkIn.containerNumber ?? '');
        setTrailerPlate(checkIn.trailerPlate ?? '');
        setError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, checkIn?.checkInId]);

    if (!checkIn) return null;

    const truck = [
        checkIn.driverName || 'Driver not named',
        checkIn.carrierCode,
        checkIn.appointmentNumber,
    ].filter(Boolean).join(' · ');

    /**
     * ⚠️ SENDS ONLY WHAT CHANGED — the same discipline as `saveEdit` in the panel,
     * and for the same reason: the endpoint treats an absent key as "leave it
     * alone", so posting all three every time would write a history line for two
     * fields nobody touched. The arrival log is the record a dispute reads.
     */
    async function save() {
        if (!checkIn) return;
        const before: Record<string, string> = {
            trailerNumber: checkIn.trailerNumber ?? '',
            containerNumber: checkIn.containerNumber ?? '',
            trailerPlate: checkIn.trailerPlate ?? '',
        };
        const now: Record<string, string> = {
            trailerNumber: trailerNumber.trim(),
            containerNumber: containerNumber.trim(),
            trailerPlate: trailerPlate.trim(),
        };
        const changes: Record<string, string | null> = {};
        for (const k of Object.keys(before)) {
            if (now[k] !== before[k]) changes[k] = now[k] === '' ? null : now[k];
        }
        if (Object.keys(changes).length === 0) { onClose(); return; }

        setBusy(true);
        setError(null);
        try {
            await editCheckIn(await getToken(), checkIn.checkInId, changes);
            onSaved();
            onClose();
        } catch (err) {
            /* ⭐ THE SERVER'S OWN SENTENCE, SHOWN WHOLE. The bypass-code refusal
               explains what 123 means and what to do instead; paraphrasing it here
               would be the fourth place that string lives. */
            setError(err instanceof Error ? err.message : 'Could not save that number.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Trailer, container or plate</DialogTitle>
                    <DialogDescription>{truck}</DialogDescription>
                </DialogHeader>

                {/**
                  * ⚠️ SHOWN ONLY WHEN HE ACTUALLY OWES ONE. The dialog also opens from
                  * the icon on any arrival — to correct a number, or to add a plate —
                  * and an amber "he owes this" banner on a truck that gave its number
                  * at the gate would be the colour spent on nothing again (#269).
                  */}
                {checkIn.vehicleNumberPending === true && (
                    <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                        This driver typed the bypass code at the gate — he was asked for a number
                        and did not have one. Anything entered here clears the alert on his card.
                    </div>
                )}

                {/**
                  * ⭐⭐ #73 — THE DISAGREEMENT, AND WHICH RECORD THESE BOXES EDIT.
                  *
                  * ⛔⛔ THE SECOND SENTENCE IS THE IMPORTANT ONE. A scheduler opening
                  * this from the mismatch row is one keystroke from "correcting" the
                  * number — and if the BOOKING is the wrong one, typing here changes the
                  * wrong record and makes the two agree on a lie, with the disagreement
                  * gone from the rail and nothing left to notice it by.
                  *   ** MAKING TWO RECORDS AGREE IS NOT THE SAME AS MAKING THEM RIGHT,
                  *      AND ONLY ONE OF THEM LEAVES A TRACE OF THE OTHER. **
                  *
                  * ⚠️ Not clickable through to the appointment. This panel has no
                  * handler that opens a load's editor, and inventing one from here would
                  * be a gesture with nowhere to go. The load is NAMED so the scheduler
                  * can reach it the way they always do.
                  */}
                {checkIn.vehicleNumberMismatch && checkIn.vehicleNumberMismatch.length > 0 && (
                    <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                        {checkIn.vehicleNumberMismatch.map(m => (
                            <div key={m.field}>
                                {checkIn.appointmentNumber ?? 'The appointment'} says the {m.field} number is{' '}
                                <span className="font-semibold">{m.booked}</span>; this truck has{' '}
                                <span className="font-semibold">{m.arriving}</span>.
                            </div>
                        ))}
                        <div className="mt-1 text-[11px]">
                            These boxes are the <span className="font-semibold">truck's</span> numbers.
                            If the appointment's number is the wrong one, edit
                            {checkIn.appointmentNumber ? ` ${checkIn.appointmentNumber}` : ' the appointment'} instead —
                            changing it here would make the two agree without either being checked.
                        </div>
                    </div>
                )}

                <div className="space-y-2.5">
                    {/* ⭐ ANY ONE OF THE THREE ANSWERS IT — said once, above the fields,
                        rather than three times as "optional" labels that would leave a
                        scheduler wondering which one is the real one. */}
                    <div className="text-[11px] text-muted-foreground">
                        Any one of these is enough. The number is what puts this truck in the
                        yard order and gets it a dock door.
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="vn-trailer">Trailer number</Label>
                        <Input
                            id="vn-trailer" value={trailerNumber} maxLength={50} disabled={busy}
                            autoFocus
                            onChange={(e) => setTrailerNumber(e.target.value.toUpperCase())}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="vn-container">Container number</Label>
                        <Input
                            id="vn-container" value={containerNumber} maxLength={50} disabled={busy}
                            onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
                        />
                    </div>
                    <div className="space-y-1">
                        {/* ⛔ 20, matching migration 161, the route's zod and COLS. The
                            column is the shortest of the three and a screen that accepts
                            more surfaces as a raw truncation error on save. */}
                        <Label htmlFor="vn-plate">License plate</Label>
                        <Input
                            id="vn-plate" value={trailerPlate} maxLength={20} disabled={busy}
                            onChange={(e) => setTrailerPlate(e.target.value.toUpperCase())}
                        />
                    </div>
                </div>

                {error && (
                    <div className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
                        {error}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button size="sm" disabled={busy} onClick={() => void save()}>
                        {busy ? 'Saving…' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
